import React, {
    useCallback, useState, useEffect, useContext
} from 'react';
import { useTranslator } from '@jutro/locale';
import { withRouter } from 'react-router-dom';
import { withAuthenticationContext } from 'gw-digital-auth-react';
import { ViewModelForm } from 'gw-portals-viewmodel-react';
import { useDependencies } from 'gw-portals-dependency-react';
import { readViewModelValue } from 'gw-jutro-adapters-react';
import {
    AdacDocumentsUpload, onDocumentsUpload, AdacButtonWithLoader, CommunicationVariablesContext
} from 'gw-capability-fnol-adac-react';
import { ErrorCodes } from 'gw-portals-util-adac-js';
import styles from './ClaimDocuments.module.scss';
import metadata from './ClaimDocuments.metadata.json5';
import documentsPageMessages from './ClaimDocuments.messages';
import { useModal } from '@jutro/components';
import usePortalModeBasedLabel from 'gw-capability-fnol-adac-react/hooks/usePortalModeBasedLabel';

function ClaimDocuments(props) {
    const { history, location } = props;
    const [isLoading, setLoadingState] = useState(false);
    const [areDocumentsSent, setDocumentsSent] = useState(false);
    const [documentsToSend, updateDocumentsToSend] = useState([]);
    const [documentsSending, setDocumentsSending] = useState(false);
    const translator = useTranslator();
    const { showAlert } = useModal();
    const [claimDetailsData, setClaimDetailsData] = useState({
        claim: { documents: [] },
        activities: []
    });
    const { ClaimService } = useDependencies('ClaimService');
    const { communicationVariables } = useContext(CommunicationVariablesContext)
    const { token, sessionUUID } = communicationVariables;

    const getHasEbsSigned = useCallback(async () => {
        if (!token || !sessionUUID) { return; }
        ClaimService.hasEbsSigned(token, sessionUUID)
            .then(
                (response) => {
                    const responseData = {
                        isEbsSigned: response
                    };
                    setClaimDetailsData(responseData);
                    setLoadingState(false);
                }
            )
            .catch((error) => {
                if (error.appErrorCode === ErrorCodes.SESSION_EXPIRED) {
                    history.push({
                        pathname: '/sessionexpired'
                    });
                } else {
                    history.push({ pathname: '/trylater' });
                }
            });
    }, [ClaimService, history, sessionUUID, token]);

    useEffect(() => {
        if (isLoading) {
            getHasEbsSigned();
        }
    }, [isLoading, getHasEbsSigned]);

    const onNext = useCallback(async () => {
        onDocumentsUpload({
            documents: documentsToSend,
            setDocumentsSending,
            setDocumentsSent,
            ClaimService,
            translator,
            token,
            history,
            updateDocumentsToSend,
            showAlert
        });
    }, [documentsToSend, setDocumentsSending, setDocumentsSent, ClaimService, translator, history, token]);

    const viewDocuments = useCallback(() => {
        history.push({
            pathname: '/my-documents'
        });
    }, [history]);

    const recall = useCallback(() => {
        history.push({
            pathname: '/recall'
        });
    }, [history]);

    const redirectToHistoryTab = useCallback(() => {
        history.push({
            pathname: '/history'
        });
    }, [history])

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const overrides = {
        pageHeaderTitle: {
            content: usePortalModeBasedLabel('pageHeaderTitle')
        },
        claimDocumentsPageLoader: {
            loaded: !isLoading,
            loadingMessage: '...'
        },
        contentContainer: {
            visible: !isLoading
        },
        uploadDocumentIcon: {
            visible: !areDocumentsSent
        },
        documentsSentConfirmation: {
            visible: areDocumentsSent
        },
        howItWorksContainer: {
            visible: areDocumentsSent
        },
        documentsLoader: {
            loaded: !documentsSending
        },
        documentsUpload: {
            documentsToSend,
            updateDocumentsToSend,
            visible: !documentsSending && !areDocumentsSent
        },
        nextButton: {
            visible: documentsToSend.length > 0,
            onClick: onNext,
            label: translator(documentsPageMessages.next),
            disabled: documentsSending,
            isLoading: documentsSending

        },
        actionOptionsContainer: {
            visible: !documentsSending
        }
    };

    const readValue = useCallback(
        (id, path) => {
            return readViewModelValue(metadata.pageContent, claimDetailsData, id, path, overrides);
        },
        [claimDetailsData, overrides]
    );

    const resolvers = {
        resolveValue: readValue,
        resolveClassNameMap: styles,
        resolveComponentMap: {
            documentsupload: AdacDocumentsUpload,
            buttonWitLoader: AdacButtonWithLoader
        },
        resolveCallbackMap: {
            onNext: onNext,
            viewDocuments,
            recall,
            redirectToHistoryTab
        }
    };

    return (
        <ViewModelForm
            uiProps={metadata.pageContent}
            model={claimDetailsData}
            overrideProps={overrides}
            resolveValue={readValue}
            componentMap={resolvers.resolveComponentMap}
            callbackMap={resolvers.resolveCallbackMap}
            classNameMap={resolvers.resolveClassNameMap}
        />
    );
}

export default withRouter(withAuthenticationContext(ClaimDocuments));
